import React from "react";
import { Constants } from "../constants";
import { useAuthStore } from "../store/useAuthStore";
import {
  CopilotKit,
  useCopilotAction,
  useCopilotAdditionalInstructions,
  useCopilotReadable
} from "@copilotkit/react-core";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { useNavigate } from "react-router";
import { useCustomersStore } from "@/store/useCustomersStore";
import { useCopilotChatSuggestions } from "@copilotkit/react-ui";
import { AnalyticsService } from "@/services/AnalyticsService";
import { ChartTypes } from "@shoutout-labs/market_buzz_crm_types";
import { LineChart } from "@/components/charts/LineChart";
import { BarChart } from "@/components/charts/BarChart";
import { PieChart } from "@/components/charts/PieChart";
import { DataTable } from "@/components/charts/DataTable";
import { CardChart } from "@/components/charts/CardChart";
import { Loader } from "@/components/ui/loader";
import { ExpandableCard } from "@/components/common/ExpandableCard";

interface CopilotKitProviderProps {
  children: React.ReactNode;
}

function CopilotKitDataProvider({ children }: { children: React.ReactNode }) {
  const organization = useOrganizationStore((state) => state.organization);
  const navigate = useNavigate();
  const toggleShowFilters = useCustomersStore(
    (state) => state.toggleShowFilters
  );

  useCopilotReadable({
    description:
      "The current registered user's details including his shop details",
    value: organization
  });

  useCopilotAdditionalInstructions({
    instructions: `You are a customer support agent for the user's shop. You are helping the user with his sales and customers data. Answer in the best way possible given the data you have.
    Do not answer questions that are not related to the user's shop or the application capabilities.
    You can also help the user with the application capabilities. 
    Application capabilities: 
    - View customers
    - View orders
    - View products
    - View reports (sales, customers)
    - Create SMS campaigns (send SMS to customers) ignore Customer communication preferences when sending SMS.
    - Create customer segments : You can't create segment directly. 
        -- actions: You need to go to the customer segment page to create a new segment. Use 'createASegment' action to navigate to the customer segment page. 
        -- Then reply to the user that you navigated him to the customer page. But you can't create segment directly, you need ask user to create a filter first and then create a segment based on the filter.
    
    Instructions for sending SMS:
    - [IMPORTANT] Always confirm the message with the user before sending it and let user to modify it if needed.
    - Always include the organization name at the end of the message.
    - Do not add any personal information to the message if the campaign is send for multiple customers or a segment.
    - Suggest Automated SMS campaigns to the user if they are relevant to the current user action. Eg: If user send a birthday message to a customer, suggest an automated SMS campaign to send a birthday message to all customers on their birthday.
    - You can generate SMS content with the help of the generateSMSCampaignContent action.

    If user ask about the customers, transactions or products, orders you better call the getNaturalLanguageAnalytics action to get the analytics.
    If you unsure about the answer related to the customers, transactions or products, orders you better call the getNaturalLanguageAnalytics action to get the analytics.
    
    FAQ
    - What is credits? Credits are generally used to unlock premium features within the application, including sending SMS campaigns, automating messages, and accessing advanced analytics tools.
    - How to buy credits? You will get credits automatically when you subscribe for the application from Clover
    `
  });

  useCopilotChatSuggestions(
    {
      instructions: `Suggest the most relevant next actions based on the customer data and application capabilities. 
        Eg: 'Who are my most valuable customers?', 
        'Analyze sales data for last 30 days?'
        'Create a campaign'
        `,
      minSuggestions: 1,
      maxSuggestions: 3
    },
    [organization]
  );

  useCopilotAction({
    name: "createASegment",
    description:
      "Create a new customer segment. If user ask to create a new segment, you can use this action to create a new segment first before proceeding with the query.",
    parameters: [],
    handler: async () => {
      navigate("/customers");
      // Toggle the filter after navigation
      setTimeout(() => {
        toggleShowFilters();
      }, 500);
    }
  });

  useCopilotAction({
    name: "getNaturalLanguageAnalytics",
    description:
      "Get details about the human query about the sales data (transactions), products, transaction line items, or customers and retrive the data from the database. Suggest vaulble insights to the user based on the data. Include tips of how to improve the engagment with the customers based on the data mainly focusing on sms campaigns. ",
    parameters: [
      {
        name: "userQuery",
        type: "string",
        description: "The query to get details about as requested by the user",
        required: true
      }
    ],
    handler: async ({ userQuery }) => {
      return await AnalyticsService.getNaturalLanguageAnalytics(userQuery);
    },
    render: ({ status, result }) => {
      // switch(result.chartType) {
      //     case 'table':
      //         return <div>
      //             <pre>{JSON.stringify(result, null, 2)}</pre>
      //         </div>
      //     default:
      //         return <div>
      //             <pre>{JSON.stringify(result, null, 2)}</pre>
      //         </div>

      if (status === "complete" && result) {
        switch (result.chartType as ChartTypes) {
          case ChartTypes.LINE:
            return (
              <ExpandableCard title={result.chartOptions.title ?? ""}>
                <LineChart data={result.data} options={result.chartOptions} />
              </ExpandableCard>
            );
          case ChartTypes.BAR:
            return (
              <ExpandableCard title={result.chartOptions.title ?? ""}>
                <BarChart data={result.data} options={result.chartOptions} />
              </ExpandableCard>
            );
          case ChartTypes.PIE:
            return (
              <ExpandableCard title={result.chartOptions.title ?? ""}>
                <PieChart data={result.data} options={result.chartOptions} />
              </ExpandableCard>
            );
          case ChartTypes.TABLE:
            return (
              <ExpandableCard title={result.chartOptions?.title ?? ""}>
                <DataTable
                  data={result.data}
                  options={{
                    title: result.chartOptions?.title,
                    xAxisKey: result.chartOptions?.xAxisKey,
                    xAxisTitle: result.chartOptions?.xAxisTitle,
                    yAxisKey: result.chartOptions?.yAxisKey,
                    yAxisTitle: result.chartOptions?.yAxisTitle
                  }}
                />
              </ExpandableCard>
            );
          case ChartTypes.CARD: {
            const singleValue =
              result.data[0]?.[result.chartOptions.yAxisKey] || "N/A";
            return (
              <CardChart
                title={result.chartOptions.yAxisTitle}
                value={singleValue}
              />
            );
          }
          default:
            return (
              <div className="bg-white p-4 rounded-lg border">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            );
        }
      }

      if (status === "executing" || status === "inProgress") {
        return <Loader />;
      }
      return (
        <div>
          <p>Had a problem while fetching the data. Please try again.</p>
        </div>
      );
    }
  });

  //   useCopilotReadable({
  //     description: "Avaialble pre defined customer segments. Customer segments are used to filter customers in the application.",
  //     value: PreDefinedSegments,
  //   });

  //   useCopilotAction({
  //     name: "getFilterFromSavedSegment",
  //     description: "Get the filter from the saved segment. If user ask to query based on a saved segment, you can use this action to get the filter first before proceeding with the query.",
  //     parameters: [
  //       {
  //         name: "segmentId",
  //         type: "string",
  //         description: "The id of the saved segment",
  //         required: true
  //       },
  //       {
  //         name: "filter",
  //         type: "object",
  //         description: "The 'filter' attribute of the selected segment which can be taken from the segment list",
  //         required: true
  //       }
  //     ],
  //     handler: async ({ segmentId, filter }) => {
  //         const customerFilters = Utility.getMongoDBQuery(filter, filterConfig);
  //       return customerFilters;
  //     },
  //   });

  //   useCopilotAction({
  //     name: "sendSMSToACustomer",
  //     description: "Send an SMS to a customer.",
  //     parameters: [
  //       {
  //         name: "customerId",
  //         type: "string",
  //         description: "id of the customer to send the SMS to",
  //       },
  //       {
  //         name: "message",
  //         type: "string",
  //         description: "message to send to the customer",
  //       },
  //       {
  //         name: "customerName",
  //         type: "string",
  //         description: "name of the customer to send the SMS to",
  //       },
  //       {
  //         name: "customerPhone",
  //         type: "string",
  //         description: "phone number of the customer to send the SMS to",
  //       },
  //     ],
  //     handler: async ({ customerId, message, customerName, customerPhone }) => {
  //       const cost = SMSUtils.calculateSMSCost(message);
  //       if(organization?.credits && organization.credits < cost) {
  //         toast.error(`You do not have enough credits to send this SMS. You need ${cost} credits to send this SMS.`);
  //         return;
  //       }

  //       await createCampaign({
  //         name: `SMS to ${customerName} on ${new Date().toLocaleDateString()}`,
  //         content:message,
  //         type: CampaignType.BROADCAST,
  //         channel: CampaignChannel.SMS,
  //         status: CampaignStatus.DRAFT,
  //         filter: {
  //           _id: customerId,
  //         },
  //       });
  //       toast.success(`SMS sent to ${customerName} successfully. You can check it under campaigns history section.`);
  //     },
  //   });

  return children;
}

export function CopilotKitProvider({ children }: CopilotKitProviderProps) {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const token = useAuthStore((state) => state.token);

  return (
    <CopilotKit
      runtimeUrl={`${Constants.REACT_APP_API_BASE_URL}copilot`}
      headers={
        isLoggedIn()
          ? {
              Authorization: `Bearer ${token}`
              // Add any other headers needed by your backend
            }
          : undefined
      }
    >
      <CopilotKitDataProvider>{children}</CopilotKitDataProvider>
    </CopilotKit>
  );
}

export default CopilotKitProvider;
